/**
 * <bw-track-list>: the shared Barkwoo track list (Oat & Mint T5, barkwoo-o0zk.12).
 * Markup: snippets/bw-track-list.liquid, used by the home Listen panel
 * (sections/bw-home-player), /pages/music (sections/barkwoo-playlist) and the
 * PDP "Hear a sample" card (sections/bw-product-details).
 *
 *   <bw-track-list data-play-label="Play: [title]" data-pause-label="Pause: [title]"
 *                  data-now-label="Now playing: [title]" data-error-label="…"
 *                  data-events="home-player" data-advance>
 *     <ol data-bw-list>
 *       <li data-bw-row data-src="…mp3" data-title="…" data-duration="4:16">
 *         <button data-bw-play aria-label="Play: …">…</button>
 *         <span data-bw-time>4:16</span>
 *         <span aria-hidden="true"><span data-bw-fill></span></span>
 *       </li>
 *     </ol>
 *     <p class="visually-hidden" role="status" data-bw-status></p>
 *   </bw-track-list>
 *
 * - Never autoplays. Nothing loads until a play press: one Audio per list, made
 *   on the first press, preload none. Each played track is one media request.
 * - One track at a time on the whole page: starting playback dispatches
 *   `barkwoo:playing` on document (detail: the element) and every other list
 *   pauses.
 * - A row's button plays its track, or pauses it while it plays. A press
 *   anywhere else on the row does the same (pointer users; keyboard users have
 *   the button). The active row gets `is-active` (mint-2, progress bar),
 *   `is-playing` while it plays, and aria-current on its button; its time reads
 *   "1:46 / 4:16" and its button label flips Play/Pause.
 * - data-advance: when a track ends, the next one plays (music page). Otherwise
 *   the row resets. A list with the bw-tracks--scroll class scrolls inside the
 *   panel; its list gets data-more while rows are hidden below (CSS fade).
 * - Analytics: data-events="home-player" | "sample" | "playlist" emits
 *   `barkwoo:<events>:play` / `barkwoo:<events>:pause` ({ title }) on document
 *   when a row starts or stops.
 * - A failed load or play shows data-error-label in the status line. Pressing
 *   play again on that track requests the file again.
 */
const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class BwTrackList extends HTMLElement {
  connectedCallback() {
    this.onOtherPlaying = (e) => {
      if (e.detail !== this && this.audio && !this.audio.paused) this.audio.pause();
    };
    document.addEventListener('barkwoo:playing', this.onOtherPlaying);
    if (this.rows) return; // re-attached after a DOM move: rows are already wired

    this.rows = Array.from(this.querySelectorAll('[data-bw-row]'));
    this.list = this.querySelector('[data-bw-list]');
    this.status = this.querySelector('[data-bw-status]');
    this.events = this.dataset.events || '';
    this.audio = null;
    this.active = null;

    // Long lists scroll inside the panel: flag "more rows below" for the fade.
    if (this.list && this.classList.contains('bw-tracks--scroll')) {
      const list = this.list;
      const more = () => list.toggleAttribute('data-more', list.scrollTop + list.clientHeight < list.scrollHeight - 2);
      list.addEventListener('scroll', more, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(more).observe(list);
      more();
    }

    this.rows.forEach((row) => {
      const play = row.querySelector('[data-bw-play]');
      if (!play) return;
      play.addEventListener('click', () => this.toggle(row));
      row.addEventListener('click', (e) => {
        if (!e.target.closest('button, a, input')) this.toggle(row);
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('barkwoo:playing', this.onOtherPlaying);
    if (this.audio) this.audio.pause();
  }

  label(key, title) {
    return (this.dataset[key] || '').replace('[title]', title);
  }

  ensureAudio() {
    if (this.audio) return this.audio;
    const audio = new Audio();
    audio.preload = 'none';
    // The UI follows the media element, so a button flips as soon as playback
    // is requested and back when anything pauses it.
    audio.addEventListener('play', () => this.setPlaying(this.active, true));
    audio.addEventListener('pause', () => this.setPlaying(this.active, false));
    audio.addEventListener('timeupdate', () => this.render());
    audio.addEventListener('loadedmetadata', () => this.render());
    audio.addEventListener('ended', () => this.ended());
    audio.addEventListener('error', () => this.fail());
    this.audio = audio;
    return audio;
  }

  async toggle(row) {
    const audio = this.ensureAudio();
    // A failed load leaves `paused` false, so check `error` too: a press on
    // the failed track retries it instead of pausing it.
    if (row === this.active && !audio.paused && !audio.error) {
      audio.pause();
      return;
    }
    if (this.status) this.status.textContent = '';
    if (row !== this.active) {
      if (this.active) {
        this.setPlaying(this.active, false);
        audio.pause();
        this.reset(this.active);
      }
      this.active = row;
      this.activate(row);
      audio.src = row.dataset.src;
    } else if (audio.error) {
      // The same track after a failed load: the element keeps its error and
      // play() would reject at once, so request the file again.
      audio.src = row.dataset.src;
      audio.load();
    }
    document.dispatchEvent(new CustomEvent('barkwoo:playing', { detail: this }));
    try {
      await audio.play();
    } catch (error) {
      // AbortError: superseded by a pause or another track. NotAllowedError:
      // the browser blocked playback; stay paused, the button still works.
      if (error && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return;
      this.fail();
    }
  }

  activate(row) {
    row.classList.add('is-active');
    const play = row.querySelector('[data-bw-play]');
    if (play) play.setAttribute('aria-current', 'true');
    this.setProgress(row, 0);
  }

  reset(row) {
    row.classList.remove('is-active', 'is-playing');
    const play = row.querySelector('[data-bw-play]');
    if (play) {
      play.removeAttribute('aria-current');
      play.setAttribute('aria-label', this.label('playLabel', row.dataset.title));
    }
    const time = row.querySelector('[data-bw-time]');
    if (time) time.textContent = row.dataset.duration || '';
    this.setProgress(row, 0);
  }

  setPlaying(row, playing) {
    if (!row || row.classList.contains('is-playing') === playing) return;
    const title = row.dataset.title || '';
    row.classList.toggle('is-playing', playing);
    const play = row.querySelector('[data-bw-play]');
    if (play) play.setAttribute('aria-label', this.label(playing ? 'pauseLabel' : 'playLabel', title));
    if (this.status) this.status.textContent = playing ? this.label('nowLabel', title) : '';
    if (this.events) {
      document.dispatchEvent(
        new CustomEvent(`barkwoo:${this.events}:${playing ? 'play' : 'pause'}`, { detail: { title } })
      );
    }
  }

  setProgress(row, ratio) {
    const fill = row.querySelector('[data-bw-fill]');
    if (fill) fill.style.setProperty('--bw-progress', String(Math.max(0, Math.min(1, ratio))));
  }

  render() {
    const row = this.active;
    if (!row || !this.audio) return;
    const cur = this.audio.currentTime;
    const dur = this.audio.duration;
    const known = Number.isFinite(dur) && dur > 0;
    const total = known ? fmt(dur) : row.dataset.duration || '';
    const time = row.querySelector('[data-bw-time]');
    if (time) time.textContent = total ? `${fmt(cur)} / ${total}` : fmt(cur);
    this.setProgress(row, known ? cur / dur : 0);
  }

  ended() {
    const row = this.active;
    if (!row) return;
    const next = this.rows[this.rows.indexOf(row) + 1];
    if (this.hasAttribute('data-advance') && next) {
      this.toggle(next);
      this.reveal(next);
      return;
    }
    this.reset(row);
    this.active = null;
  }

  // Scroll a long list (not the page) so an auto-advanced row is in view.
  reveal(row) {
    const list = this.list;
    if (!list || list.scrollHeight <= list.clientHeight) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    const behavior = reducedMotion() ? 'auto' : 'smooth';
    if (top < list.scrollTop) list.scrollTo({ top, behavior });
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTo({ top: bottom - list.clientHeight, behavior });
  }

  fail() {
    const row = this.active;
    if (!row) return;
    this.setPlaying(row, false);
    if (this.status) this.status.textContent = this.dataset.errorLabel || '';
  }
}

if (!customElements.get('bw-track-list')) customElements.define('bw-track-list', BwTrackList);
